import React, {useEffect, useState} from 'react';
import './index.scss';
import Input from '../Input';
import Checkbox from '../Checkbox';
import CustomSelect from '../CustomSelect';
import {
  useGetPeriodsQuery,
  useCreateInspectionMutation,
  useCreateAgencyMutation,
  useUpdateAgancyMutation,
} from '../../store/builder/inspections.api';
import {toast} from 'react-toastify';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {useDispatch, useSelector} from 'react-redux';
import {useGetRevisions} from '../../pages/EquipmentType/model/revisions/getRevisions';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';

// The agency's temperature unit, stored by the API as `fahrenheit` / `celsius`
// and stamped onto every temperature field of an inspection. The unit names are
// the same in both languages, only the field label is translated - and the
// translations come from the backend, so a key it does not ship with yet would
// render as the raw key.
const TEMP_UNIT_OPTIONS = [
  {value: 'fahrenheit', label: 'Fahrenheit (°F)'},
  {value: 'celsius', label: 'Celsius (°C)'},
];
const TEMP_UNIT_LABEL_KEY = 'modal.tempUnit';
const TEMP_UNIT_LABEL_FALLBACK = 'Temperature Unit';
const DEFAULT_TEMP_UNIT = 'fahrenheit';

export default function Modal({
  title,
  exit,
  type,
  currentItem,
  setCurrentItem,
  timezones,
  setChosenPhoto,
  setShowModal,
  photo,
  setPhoto,
  imageBlob,
  getAgencies,
  isAdmin = true,
  waterKitData,
}) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [inspection, setInspection] = useState({
    name: null,
    period: null,
    is_for_equipment: false,
  });
  const [typeOptions, setTypeOptions] = useState([
    {value: false, label: t('modal.createInspection.typeOptions.facility')},
    {value: true, label: t('modal.createInspection.typeOptions.equipment')},
  ]);
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const [timezone, setTimezone] = useState(null);
  const [airport, setAirport] = useState('');
  const [sso, setSso] = useState('');
  const [hydrantSystems, setHydrantSystems] = useState(false);
  const [allowDailyDecEquipment, setAllowDailyDecEquipment] = useState(false);
  const [allowGeneratingDecommissioned, setAllowGeneratingDecommissioned] = useState(false);
  const [partialInspectionsNotification, setPartialInspectionsNotification] = useState(false);
  const [partialInspectionsNotificationMinutes, setPartialInspectionsNotificationMinutes] = useState(0);
  const [upstreamTests, setUpstreamTests] = useState(false);
  const [hasCorrectedDp, setHasCorrectedDp] = useState(false);
  const [todayIsDay, setTodayIsDay] = useState(false);
  const {data, isError, isFetching, error, isSuccess} = useGetPeriodsQuery();
  const [time, setTime] = useState(null);
  const [notificationTime, setNotificationTime] = useState(currentItem?.upcoming_oos_notification_time);
  const [startDayIsTodayOptions, setStartDayIsTodayOptions] = useState([
    {value: true, label: 'Same Day'},
    {value: false, label: 'Next Day'},
  ]);
  const [solidRating, setSolidRating] = useState([
    {
      value: 1,
      label: '1. Clean',
    },
    {
      value: 2,
      label: '2. Slight',
    },
    {
      value: 3,
      label: '3. Particulate',
    },
    {
      value: 4,
      label: '4. Dirty',
    },
  ]);
  const [waterRating, setWaterRating] = useState([
    {
      value: 'A',
      label: 'A. Bright',
    },
    {
      value: 'B',
      label: 'B. Hazy',
    },
    {
      value: 'C',
      label: 'C. Cloudy',
    },
    {
      value: 'D',
      label: 'D. Wet',
    },
    {
      value: 'E',
      label: 'E. Surfactants',
    },
  ]);
  const [timeOptions, setTimeOptions] = useState([
    {
      label: '12:00 AM',
      value: 0,
    },
    {
      label: '1:00 AM',
      value: 60,
    },
    {
      label: '2:00 AM',
      value: 120,
    },
    {
      label: '3:00 AM',
      value: 180,
    },
    {
      label: '4:00 AM',
      value: 240,
    },
    {
      label: '5:00 AM',
      value: 300,
    },
    {
      label: '6:00 AM',
      value: 360,
    },
    {
      label: '7:00 AM',
      value: 420,
    },
    {
      label: '8:00 AM',
      value: 480,
    },
    {
      label: '9:00 AM',
      value: 540,
    },
    {
      label: '10:00 AM',
      value: 600,
    },
    {
      label: '11:00 AM',
      value: 660,
    },
    {
      label: '12:00 PM',
      value: 720,
    },
    {
      label: '1:00 PM',
      value: 780,
    },
    {
      label: '2:00 PM',
      value: 840,
    },
    {
      label: '3:00 PM',
      value: 900,
    },
    {
      label: '4:00 PM',
      value: 960,
    },
    {
      label: '5:00 PM',
      value: 1020,
    },
    {
      label: '6:00 PM',
      value: 1080,
    },
    {
      label: '7:00 PM',
      value: 1140,
    },
    {
      label: '8:00 PM',
      value: 1200,
    },
    {
      label: '9:00 PM',
      value: 1260,
    },
    {
      label: '10:00 PM',
      value: 1320,
    },
    {
      label: '11:00 PM',
      value: 1380,
    },
  ]);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [fboEmail, setFboEmail] = useState(null);
  const [areaDirector, setAreaDirector] = useState(null);
  const [svp, setSvp] = useState(null);
  const [inspectionRevision, setInspectionRevision] = useState(null);
  const [fboData, setFboData] = useState({
    nozzle_fueling_pressure: null,
    nozzle_fueling_pressure_decommission: null,
    sump_solid_rating: null,
    sump_water_rating: null,
    sump_trend_alert: null,
    daily_weekly_grace_period: null,
    water_test_kit_id: null,
    nozzle_screen_cleaning_method: null,
    upcoming_oos_notification_time: null,
    inactivity_timeout_mobile: 0,
    inactivity_timeout_web: 0,
    is_pin_enabled: false,
    pin_require_seconds: null,
    partial_inspections_notification_minutes: 0,
    partial_inspections_notification: false,
    temp_unit: DEFAULT_TEMP_UNIT,
  });
  const [
    createInspection,
    {
      data: createInspectionData,
      isError: createInspectionIsError,
      isLoading: createInspectionIsLoading,
      isSuccess: createInspectionIsSuccess,
      error: createInspectionError,
    },
  ] = useCreateInspectionMutation();

  const [
    createAgancy,
    {
      data: createAgancyData,
      isError: createAgancyIsError,
      isLoading: createAgancyIsLoading,
      isSuccess: createAgancyIsSuccess,
      error: createAgancyError,
    },
  ] = useCreateAgencyMutation();

  const [
    updateAgency,
    {
      data: updateAgancyData,
      isError: updateAgancyIsError,
      isLoading: updateAgancyIsLoading,
      isSuccess: updateAgancyIsSuccess,
      error: updateAgancyError,
    },
  ] = useUpdateAgancyMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    createInspectionError,
    error,
    createAgancyError,
    updateAgancyError,
  ]);
  const [nozzleScreenOptions] = useState([
    {value: 1, label: '1'},
    {value: 2, label: '2'},
    {value: 3, label: '3'},
  ]);
  const [getRevisions, revisionOptions] = useGetRevisions();

  const isFieldDisabled = fieldKey => currentItem?.disabled_fields?.includes(fieldKey);
  useLoader([
    createInspectionIsLoading,
    createAgancyIsLoading,
    updateAgancyIsLoading,
  ]);

  useEffect(() => {
    getRevisions(false, true);
  }, []);

  useEffect(() => {
    if (createInspectionIsSuccess) {
      exit();
      toast.success(createInspectionData?.message);
    }
    if (createInspectionIsError) {
      toast.error(
        createInspectionError.data.period
          ? createInspectionError.data.period[0]
          : 'Error',
      );
    }
    if (createAgancyIsSuccess) {
      exit();
      toast.success(createAgancyData?.data?.message);
      getAgencies();
    }
  }, [
    createInspectionIsSuccess,
    createInspectionIsError,
    createAgancyIsSuccess,
    createAgancyIsError,
    updateAgancyIsSuccess,
  ]);
  useEffect(() => {
    if (currentItem?.title) {
      setInspection(old => ({...old, name: currentItem?.title}));
    }
    if (currentItem?.timezone) {
      setTimezone(currentItem?.timezone);
    }
    if (currentItem?.airport_id) {
      setAirport(currentItem?.airport_id);
    }
    if (currentItem?.sso_site_code) {
      setSso(currentItem?.sso_site_code);
    }
    if (currentItem?.fbo_email) {
      setFboEmail(currentItem?.fbo_email);
    }
    if (currentItem?.nozzle_fueling_pressure) {
      setFboData(old => ({
        ...old,
        nozzle_fueling_pressure: currentItem?.nozzle_fueling_pressure
          ? currentItem?.nozzle_fueling_pressure
          : null,
      }));
    }
    if (currentItem?.nozzle_fueling_pressure_decommission) {
      setFboData(old => ({
        ...old,
        nozzle_fueling_pressure_decommission:
          currentItem?.nozzle_fueling_pressure_decommission
            ? currentItem?.nozzle_fueling_pressure_decommission
            : null,
      }));
    }
    if (currentItem?.sump_solid_rating) {
      setFboData(old => ({
        ...old,
        sump_solid_rating: currentItem?.sump_solid_rating
          ? currentItem?.sump_solid_rating
          : null,
      }));
    }
    if (currentItem?.sump_water_rating) {
      setFboData(old => ({
        ...old,
        sump_water_rating: currentItem?.sump_water_rating
          ? currentItem?.sump_water_rating
          : null,
      }));
    }
    if (currentItem?.sump_trend_alert) {
      setFboData(old => ({
        ...old,
        sump_trend_alert: currentItem?.sump_trend_alert
          ? currentItem?.sump_trend_alert
          : null,
      }));
    }
    if (currentItem?.nozzle_screen_cleaning_method) {
      setFboData(old => ({
        ...old,
        nozzle_screen_cleaning_method:
          currentItem?.nozzle_screen_cleaning_method
            ? currentItem?.nozzle_screen_cleaning_method
            : null,
      }));
    }
    if (currentItem?.upcoming_oos_notification_time) {
      setFboData(old => ({
        ...old,
        upcoming_oos_notification_time:
          currentItem?.upcoming_oos_notification_time
            ? currentItem?.upcoming_oos_notification_time
            : null,
      }));
    }
    if (
      currentItem?.daily_weekly_grace_period !== null &&
      currentItem?.daily_weekly_grace_period !== undefined
    ) {
      setFboData(old => ({
        ...old,
        daily_weekly_grace_period:
          currentItem?.daily_weekly_grace_period !== null &&
          currentItem?.daily_weekly_grace_period !== undefined
            ? currentItem?.daily_weekly_grace_period
            : null,
      }));
    }
    if (currentItem?.water_test_kit_id) {
      setFboData(old => ({
        ...old,
        water_test_kit_id: currentItem?.water_test_kit_id
          ? currentItem?.water_test_kit_id
          : null,
      }));
    }
    if (currentItem?.temp_unit) {
      setFboData(old => ({...old, temp_unit: currentItem?.temp_unit}));
    }
    if (currentItem?.has_hydrant_systems) {
      setHydrantSystems(currentItem?.has_hydrant_systems);
    }
    if (currentItem?.allow_daily_dec_equipment) {
      setAllowDailyDecEquipment(currentItem?.allow_daily_dec_equipment);
    }
    if (currentItem?.allow_generating_decommissioned) {
      setAllowGeneratingDecommissioned(currentItem?.allow_generating_decommissioned);
    }
    if (currentItem?.partial_inspections_notification) {
      setPartialInspectionsNotification(currentItem?.partial_inspections_notification);
    }
    if (currentItem?.partial_inspections_notification_minutes) {
      setPartialInspectionsNotificationMinutes(currentItem?.partial_inspections_notification_minutes);
    }
    if (currentItem?.has_upstream_tests) {
      setUpstreamTests(currentItem?.has_upstream_tests);
    }
    if (currentItem?.has_corrected_dp) {
      setHasCorrectedDp(currentItem?.has_corrected_dp);
    }
    if (currentItem?.fbo_phone) {
      setPhoneNumber(currentItem?.fbo_phone?.replace(/\D/g, '').slice(1));
    }
    if (currentItem?.area_director) {
      setAreaDirector(currentItem?.area_director);
    }
    if (currentItem?.svp) {
      setSvp(currentItem?.svp);
    }

    if (typeof currentItem?.start_day_minutes === 'number') {
      setTime(currentItem?.start_day_minutes);
    }
    if (currentItem?.upcoming_oos_notification_time) {
      setNotificationTime(+currentItem?.upcoming_oos_notification_time);
    }
    if (currentItem?.inactivity_timeout_mobile) {
      setFboData(old => ({...old, inactivity_timeout_mobile: currentItem?.inactivity_timeout_mobile}));
    }
    if (currentItem?.inactivity_timeout_web) {
      setFboData(old => ({...old, inactivity_timeout_web: currentItem?.inactivity_timeout_web}));
    }
    setFboData(old => ({
      ...old,
      is_pin_enabled: !!currentItem?.is_pin_enabled,
      pin_require_seconds: currentItem?.pin_require_seconds ?? null,
    }));
    // setTodayIsDay(currentItem?.start_day_is_today);
  }, [currentItem]);

  useEffect(() => {
    if (currentItem?.logo) {
      setPhoto(currentItem?.logo);
    }
  }, []);

  const handleCreateChange = e => {
    setInspection(old => ({...old, [e.target.name]: e.target.value}));
  };

  const handleTimezone = e => {
    setTimezone(e.target.value);
  };
  const handleAirport = e => {
    setAirport(e.target.value);
  };

  const handleSSO = e => {
    setSso(e.target.value);
  };

  const handleTime = e => {
    setTime(e.target.value);
  };
  const handleNotificationTime = e => {
    setNotificationTime(e.target.value);
  };
  const handlePartialInspectionsNotificationMinutes = e => {
    setPartialInspectionsNotificationMinutes(e.target.value);
  };
  const handleDayIsToday = e => {
    setTodayIsDay(e.target.value);
  };

  const handleFboData = e => {
    if (e.target.name === 'partial_inspections_notification') {
      setPartialInspectionsNotification(e.target.checked);
    } else if (e.target.name === 'partial_inspections_notification_minutes') {
      setPartialInspectionsNotificationMinutes(e.target.value);
    } else {
    setFboData(old => ({...old, [e.target.name]: e.target.value}));
    }
  };

  const handleRevisionData = selected => {
    setInspectionRevision(selected?.code);
  };

  // The PIN has to be asked before the session is dropped, so it must stay
  // under the mobile timeout. The PIN delay is in seconds while the timeout is
  // in minutes; a mobile timeout of 0 never expires, so there is nothing to
  // compare against. Mirrors the agency settings rules on the API.
  const pinRequireSeconds = Number(fboData?.pin_require_seconds);
  const mobileTimeout = Number(fboData?.inactivity_timeout_mobile);
  const pinRequireSecondsError = !fboData?.is_pin_enabled
    ? null
    : !fboData?.pin_require_seconds || !(pinRequireSeconds >= 1)
      ? t('modal.pinRequireSecondsRequired')
      : mobileTimeout > 0 && pinRequireSeconds >= mobileTimeout * 60
        ? t('modal.pinRequireSecondsLessThanMobileTimeout')
        : null;

  const submitCreateInspection = e => {
    e.preventDefault();
    if (type === 'agancy') {
      if (pinRequireSecondsError) {
        toast.error(pinRequireSecondsError);
        return;
      }
      let formData = new FormData();
      formData.append('title', inspection?.name);
      formData.append('timezone', timezone);
      formData.append('airport_id', airport);
      formData.append('sso_site_code', sso);
      formData.append('has_hydrant_systems', hydrantSystems ? 1 : 0);
      formData.append('allow_daily_dec_equipment', allowDailyDecEquipment ? 1 : 0);
      formData.append('allow_generating_decommissioned', allowGeneratingDecommissioned ? 1 : 0);
      formData.append('partial_inspections_notification_minutes', partialInspectionsNotificationMinutes);
      formData.append('partial_inspections_notification', partialInspectionsNotification ? 1 : 0);
      formData.append('has_upstream_tests', upstreamTests ? 1 : 0);
      formData.append('has_corrected_dp', hasCorrectedDp ? 1 : 0);
      formData.append(
        'daily_weekly_grace_period',
        fboData?.daily_weekly_grace_period,
      );
      formData.append(
        'water_test_kit_id',
        fboData?.water_test_kit_id === null ? 0 : fboData?.water_test_kit_id,
      );
      formData.append('temp_unit', fboData?.temp_unit ?? DEFAULT_TEMP_UNIT);
      formData.append('start_day_minutes', time);
      formData.append('upcoming_oos_notification_time', notificationTime);
      // formData.append("start_day_is_today", todayIsDay === true ? 1 : 0);
      formData.append('fbo_email', fboEmail);
      formData.append('fbo_phone', '+1' + phoneNumber?.replace(/\D/g, ''));
      formData.append(
        'nozzle_fueling_pressure',
        fboData?.nozzle_fueling_pressure,
      );
      formData.append(
        'nozzle_fueling_pressure_decommission',
        fboData?.nozzle_fueling_pressure_decommission,
      );
      formData.append(
        'nozzle_screen_cleaning_method',
        fboData?.nozzle_screen_cleaning_method,
      );
      if (fboData?.sump_solid_rating) {
        formData.append('sump_solid_rating', fboData?.sump_solid_rating);
      }
      if (fboData?.sump_water_rating) {
        formData.append('sump_water_rating', fboData?.sump_water_rating);
      }
      if (inspectionRevision || currentItem?.inspections_revision) {
        formData.append(
          'inspections_revision',
          inspectionRevision ?? currentItem?.inspections_revision,
        );
      }
      if (fboData?.sump_trend_alert) {
        formData.append('sump_trend_alert', fboData?.sump_trend_alert);
      }
      formData.append(
        'inactivity_timeout_mobile',
        fboData?.inactivity_timeout_mobile ?? 0,
      );
      formData.append(
        'inactivity_timeout_web',
        fboData?.inactivity_timeout_web ?? 0,
      );
      formData.append('is_pin_enabled', fboData?.is_pin_enabled ? 1 : 0);
      if (fboData?.is_pin_enabled) {
        formData.append('pin_require_seconds', fboData?.pin_require_seconds ?? '');
      }
      formData.append('area_director', areaDirector || '');
      formData.append('svp', svp || '');
      if (!currentItem) {
        formData.append('trial_end_date', fboData?.trial_end_date ?? null);
      }
      if (imageBlob !== null) {
        formData.append('logo', imageBlob);
      }
      if (!currentItem) {
        if (fboData?.sump_solid_rating) {
          createAgancy(formData);
        } else {
          toast.error(t('modal.createInspection.messages.sumpSolidRatingRequired'));
        }
      } else {

        updateAgency({id: currentItem?.id, data: formData})
          .unwrap()
          .then(res => {
            toast.success(res?.message);
            if (res?.error?.status !== 422 && res?.error?.status !== 500) {
              exit();
              setCurrentItem(null);
              getAgencies();
            }
          });
      }
    } else {
      if (inspection?.name?.length > 1 && inspection?.period) {
        createInspection(inspection);
        } else {
          toast.error(t('modal.createInspection.messages.fillAllFields'));
        }
    }
  };

  const cropperHandler = e => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setChosenPhoto(reader.result);
    };
    reader.readAsDataURL(files[0]);
    setShowModal(true);
    document.querySelector('.modal-fbo').style.display = 'none';
  };

  const handleChange = event => {
    let formattedPhone = event.target.value.replace(/\D/g, ''); // remove non-numeric characters
    formattedPhone = formattedPhone.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '($1) $2-$3',
    ); // add phone number formatting
    setPhoneNumber(formattedPhone);
  };

  const fboEmailHandler = e => {
    setFboEmail(e.target.value);
  };

  const areaDirectorHandler = e => {
    setAreaDirector(e.target.value);
  };

  const svpHandler = e => {
    setSvp(e.target.value);
  };
  return (
    <div className='modal modal-fbo'>
      <div className='modal__content'>
        <div className='modal__content-head'>
          <p>{!currentItem ? title : t('modal.updateFbo')}</p>
          <img
            className='modal__content-exit'
            src='/assets/icons/close.svg'
            alt='close'
            onClick={exit}
          />
        </div>
        <div
          className={`modal__content-body ${type === 'agancy' ? 'user' : ''}`}
        >
          <Input
            containerWidth={'100%'}
            id='name'
            name='name'
            placeholder={
              type === 'inspection-form'
                ? t('modal.createInspection.inspectionName')
                : t('modal.fboName')
            }
            label={
              type === 'inspection-form'
                ? t('modal.createInspection.inspectionName')
                : t('modal.fboName')
            }
            type={'text'}
            onChange={handleCreateChange}
            defaultValue={inspection?.name}
            disabled={isFieldDisabled('name')}
          />
          {type == 'agancy' && (
            <Input
              id='fbo_email'
              name='fbo_email'
              placeholder={t('modal.fboEmail')}
              label={'FBO Email'}
              type={'email'}
              onChange={fboEmailHandler}
              defaultValue={fboEmail}
              disabled={isFieldDisabled('fbo_email')}
            />
          )}
          {/* {type === "agancy" && (
            <div className="auth__field">
              <label htmlFor="fbo_email" className="auth__label fbo">
                {t('modal.fboEmail')}
              </label>
              <input
                id="fbo_email"
                placeholder={t('modal.fboEmail')}
                type="text"
                className="auth__input"
                onChange={fboEmailHandler}
                defaultValue={currentItem ? currentItem?.fbo_email : ""}
              />
            </div>
          )} */}

          {type !== 'agancy' && (
            <CustomSelect
              label={t('modal.createInspection.inspectionPeriod')}
              options={data?.data}
              name={'period'}
              onChange={handleCreateChange}
              disabled={isFieldDisabled('period')}
            />
          )}
          {type !== 'agancy' && (
            <CustomSelect
              label={t('modal.createInspection.type')}
              options={typeOptions}
              name={'is_for_equipment'}
              onChange={handleCreateChange}
              disabled={isFieldDisabled('is_for_equipment')}
            />
          )}

          {type === 'agancy' && (
            <CustomSelect
              label={t('modal.timezone')}
              options={timezones}
              name={'timezone'}
              onChange={handleTimezone}
              defaultValue={
                currentItem
                  ? {value: currentItem.timezone, label: currentItem.timezone}
                  : null
              }
              disabled={isFieldDisabled('timezone')}
            />
          )}
          {type === 'agancy' && (
            <div style={{marginTop: 16}}>
              <Input
                label={t('modal.airportId')}
                name={'airport_id'}
                placeholder={t('modal.airportId')}
                onChange={handleAirport}
                defaultValue={currentItem ? currentItem.airport_id : null}
                disabled={isFieldDisabled('airport_id')}
              />
            </div>
          )}
          {currentItem?.show_sso_site_code && userInfo?.is_global && (
            <div style={{marginTop: 16}}>
              <Input
                label={t('modal.ssoSiteCode')}
                name={'sso_site_code'}
                placeholder={t('modal.ssoSiteCode')}
                onChange={handleSSO}
                defaultValue={currentItem ? currentItem.sso_site_code : null}
                disabled={isFieldDisabled('sso_site_code')}
              />
            </div>
          )}
          <div style={{marginTop: 16, marginBottom: 16}}>
            <CustomSelect
              tooltip={true}
              tooltipText={
                t('modal.whenDoYouWantNewInspectionsToGenerateTooltip')
              }
              label={t('modal.whenDoYouWantNewInspectionsToGenerate')}
              options={timeOptions}
              name={'start_day_minutes'}
              onChange={handleTime}
              defaultValue={
                currentItem
                  ? timeOptions.filter(
                      el => el.value === currentItem?.start_day_minutes,
                    )[0]
                  : null
              }
              disabled={isFieldDisabled('start_day_minutes')}
            />
            <CustomSelect
              tooltip={false}
              label={t('modal.upcomingOutOfServiceEquipmentsNotificationTime')+'ffd'}
              options={timeOptions}
              name={'upcoming_oos_notification_time'}
              onChange={handleNotificationTime}
              defaultValue={{
                value: notificationTime,
                label: timeOptions.find(item => +item?.value === +notificationTime)?.label,
              }}
            />
            <div style={{marginTop: 16}}>
              <CustomSelect
                key={nozzleScreenOptions}
                label={t('modal.nozzleScreenCleaningMethod')}
                name={'nozzle_screen_cleaning_method'}
                options={nozzleScreenOptions}
                onChange={handleFboData}
                defaultValue={
                  currentItem
                    ? nozzleScreenOptions?.filter(
                        el =>
                          el.value ==
                          currentItem?.nozzle_screen_cleaning_method?.toString(),
                      )[0]
                    : null
                }
                disabled={isFieldDisabled('nozzle_screen_cleaning_method')}
              />
            </div>
            <div style={{marginTop: 16}}>
              {/* area_director and svp */}
                {
                  !currentItem?.hidden_fields?.includes('area_director') && (
                    <Input
                      id='area_director'
                      name='area_director'
                      placeholder={t('fboProfile.fields.areaDirectorPlaceholder')}
                      label={t('fboProfile.fields.areaDirector')}
                      type={'text'}
                      containerWidth={'93%'}
                      onChange={areaDirectorHandler}
                      defaultValue={currentItem ? currentItem?.area_director : ''}
                      maxwidth={'100%'}
                      disabled={isFieldDisabled('area_director')}
                    />
                  )
                }
                {
                  !currentItem?.hidden_fields?.includes('svp') && (
                    <Input
                  id='svp'
                  name='svp'
                  placeholder={t('fboProfile.fields.svpPlaceholder')}
                  label={t('fboProfile.fields.svp')}
                  type={'text'}
                  containerWidth={'93%'}
                  onChange={svpHandler}
                  defaultValue={currentItem ? currentItem?.svp : ''}
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('svp')}
                />
                  )
                }
            </div>
          </div>
          {isAdmin && (
            <>
              {type == 'agancy' && (
                <Input
                  tooltip={true}
                  tooltipText={t('modal.nozzleFuelingPressure')}
                  id='nozzle_fueling_pressure'
                  name='nozzle_fueling_pressure'
                  placeholder={t('modal.nozzleFuelingPressure')}
                  label={t('modal.nozzleFuelingPressure')}
                  type={'text'}
                  containerWidth={'93%'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem ? currentItem?.nozzle_fueling_pressure : ''
                  }
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('nozzle_fueling_pressure')}
                />
              )}
              {type == 'agancy' && (
                <Input
                  tooltip={true}
                  tooltipText={t('modal.nozzleFuelingPressureDecommission')}
                  id='nozzle_fueling_pressure_decommission'
                  name='nozzle_fueling_pressure_decommission'
                  placeholder={t('modal.nozzleFuelingPressureDecommission')}
                  label={t('modal.nozzleFuelingPressureDecommission')}
                  type={'text'}
                  containerWidth={'93%'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem
                      ? currentItem?.nozzle_fueling_pressure_decommission
                      : ''
                  }
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('nozzle_fueling_pressure_decommission')}
                />
              )}

              {type == 'agancy' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 20,
                  }}
                >
                  <CustomSelect
                    tooltip={true}
                    tooltipText={
                      t('modal.sumpSolidRatingTooltip')
                    }
                    maxwidth={231}
                    id='sump_solid_rating'
                    name='sump_solid_rating'
                    placeholder={t('modal.sumpSolidRating')}
                    label={t('modal.sumpSolidRating')}
                    options={solidRating}
                    onChange={handleFboData}
                    defaultValue={
                      currentItem
                        ? solidRating.filter(
                            el => el.value == currentItem?.sump_solid_rating,
                          )[0]
                        : ''
                    }
                    disabled={isFieldDisabled('sump_solid_rating')}
                  />
                  <CustomSelect
                    tooltip={true}
                    tooltipText={
                      t('modal.sumpWaterRatingTooltip')
                    }
                    maxwidth={231}
                    id='sump_water_rating'
                    name='sump_water_rating'
                    placeholder={t('modal.sumpWaterRating')}
                    label={t('modal.sumpWaterRating')}
                    options={waterRating}
                    onChange={handleFboData}
                    defaultValue={
                      currentItem
                        ? waterRating.filter(
                            el => el.value == currentItem?.sump_water_rating,
                          )[0]
                        : ''
                    }
                    disabled={isFieldDisabled('sump_water_rating')}
                  />
                </div>
              )}
              {type == 'agancy' && (
                <Input
                  tooltip={true}
                  tooltipText={
                    t('modal.sumpTrendAlertTooltip')
                  }
                  id='sump_trend_alert'
                  name='sump_trend_alert'
                  placeholder={t('modal.sumpTrendAlert')}
                  label={t('modal.sumpTrendAlert')}
                  type={'text'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem ? currentItem?.sump_trend_alert : ''
                  }
                  disabled={isFieldDisabled('sump_trend_alert')}
                />
              )}
              {type == 'agancy' && (
                <Input
                  tooltip={true}
                  tooltipText={t('modal.dailyWeeklyGracePeriodTooltip')}
                  id='daily_weekly_grace_period'
                  name='daily_weekly_grace_period'
                  placeholder={t('modal.dailyWeeklyGracePeriod')}
                  label={t('modal.dailyWeeklyGracePeriod')}
                  type={'number'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem ? currentItem?.daily_weekly_grace_period : ''
                  }
                  disabled={isFieldDisabled('daily_weekly_grace_period')}
                />
              )}
              {type == 'agancy' && (
                <div style={{marginBottom: 20}}>
                  <CustomSelect
                    label={t('modal.waterTestKit')}
                    options={waterKitData}
                    name={'water_test_kit_id'}
                    onChange={handleFboData}
                    defaultValue={
                      currentItem
                        ? waterKitData?.filter(
                            el =>
                              el.value ==
                              currentItem?.water_test_kit_id?.toString(),
                          )[0]
                        : null
                    }
                    disabled={isFieldDisabled('water_test_kit_id')}
                  />
                </div>
              )}
              {type == 'agancy' && (
                <div style={{marginBottom: 20}}>
                  <CustomSelect
                    label={
                      t(TEMP_UNIT_LABEL_KEY) === TEMP_UNIT_LABEL_KEY
                        ? TEMP_UNIT_LABEL_FALLBACK
                        : t(TEMP_UNIT_LABEL_KEY)
                    }
                    options={TEMP_UNIT_OPTIONS}
                    name={'temp_unit'}
                    onChange={handleFboData}
                    value={fboData?.temp_unit ?? DEFAULT_TEMP_UNIT}
                    disabled={isFieldDisabled('temp_unit')}
                  />
                </div>
              )}
              {type == 'agancy' && (
                <div style={{marginBottom: 20}}>
                  <CustomSelect
                    key={revisionOptions}
                    // disabled={userInfo?.agency?.has_corporate_settings}
                    label={t('modal.inspectionsRevision')}
                    name={'inspection_revisions'}
                    options={revisionOptions?.filter(item => item?.in_settings)}
                    onChange={handleRevisionData}
                    defaultValue={
                      currentItem
                        ? revisionOptions
                            ?.filter(item => item?.in_settings)
                            ?.filter(
                              el =>
                                el?.code == currentItem?.inspections_revision,
                            )[0]
                        : null
                    }
                  />
                </div>
              )}

              <div className='facility-modal__field' style={{marginTop: 16}}>
                <Checkbox
                  disabled={
                    isFieldDisabled('has_upstream_tests')
                  }
                  key={'has_upstream_tests'}
                  label={t('modal.hasUpstreamTests')}
                  id={'has_upstream_tests'}
                  checked={upstreamTests}
                  onChange={e => {
                    setUpstreamTests(!upstreamTests);
                  }}
                />
              </div>

              <div className='facility-modal__field'>
                <Checkbox
                  disabled={
                    isFieldDisabled('has_corrected_dp')
                  }
                  key={'has_corrected_dp'}
                  label={t('modal.hasCorrectedDpForWeeklyAvgas')}
                  id={'has_corrected_dp'}
                  checked={hasCorrectedDp}
                  onChange={e => {
                    setHasCorrectedDp(!hasCorrectedDp);
                  }}
                />
              </div>
              <div className='facility-modal__field'>
                <Checkbox
                  disabled={
                    isFieldDisabled('has_hydrant_systems')
                  }
                  checked={hydrantSystems}
                  key={'has_hydrant_systems'}
                  label={t('modal.hasHydrantSystems')}
                  id={'has_hydrant_systems'}
                  onChange={e => {
                    setHydrantSystems(!hydrantSystems);
                  }}
                />
              </div>
              <div className='facility-modal__field'>
                <Checkbox
                  disabled={isFieldDisabled('allow_daily_dec_equipment')}
                  checked={allowDailyDecEquipment}
                  key={'allow_daily_dec_equipment'}
                  label={t('modal.enableGenerationOfDailyInspectionsForOutOfServiceEquipment')}
                  id={'allow_daily_dec_equipment'}
                  onChange={() => {
                    setAllowDailyDecEquipment(!allowDailyDecEquipment);
                  }}
                />
              </div>
            </>
          )}
          {
                userInfo?.is_global && (
                 <div className='facility-modal__field'>
                  <Checkbox
                    disabled={isFieldDisabled('allow_generating_decommissioned')}
                    checked={allowGeneratingDecommissioned}
                    key={'allow_generating_decommissioned'}
                    label={t('modal.generateRtsInspectionsForOos')}
                    id={'allow_generating_decommissioned'}
                    onChange={() => {
                      setAllowGeneratingDecommissioned(!allowGeneratingDecommissioned);
                    }}
                />
                {/* partial_inspections_notification */}
                <Checkbox
                    disabled={isFieldDisabled('partial_inspections_notification')}
                    checked={partialInspectionsNotification}
                    key={'partial_inspections_notification'}
                    label={t('modal.partialInspectionsNotification')}
                    id={'partial_inspections_notification'}
                    onChange={() => {
                      setPartialInspectionsNotification(!partialInspectionsNotification);
                    }}
                />
                </div> 
                )
          }
          {
            userInfo?.is_global && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* partial_inspections_notification_minutes */}
                <CustomSelect
                  tooltip={false}
                  label={t('modal.partialInspectionsNotificationMinutes')}
                  options={timeOptions}
                  name={'partial_inspections_notification_minutes'}
                  onChange={handlePartialInspectionsNotificationMinutes}
                  defaultValue={
                    currentItem?.partial_inspections_notification_minutes
                      ? timeOptions.find(item => +item?.value === +currentItem?.partial_inspections_notification_minutes)
                      : null
                  }
                  disabled={!partialInspectionsNotification}
                />
                <div style={{marginTop: 16}}>
                  <Input
                  id='inactivity_timeout_mobile'
                  name='inactivity_timeout_mobile'
                  placeholder={t('modal.inactivityTimeoutMobile')}
                  label={t('modal.inactivityTimeoutMobile')}
                  type={'number'}
                  containerWidth={'93%'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem ? currentItem?.inactivity_timeout_mobile : ''
                  }
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('inactivity_timeout_mobile')}
                />
                </div>
                <div style={{marginTop: 16}}>
                  <Checkbox
                    key={'is_pin_enabled'}
                    id={'is_pin_enabled'}
                    label={t('modal.isPinEnabled')}
                    checked={!!fboData?.is_pin_enabled}
                    disabled={isFieldDisabled('is_pin_enabled')}
                    onChange={e =>
                      setFboData(old => ({
                        ...old,
                        is_pin_enabled: e.target.checked,
                      }))
                    }
                  />
                </div>
                <Input
                  id='pin_require_seconds'
                  name='pin_require_seconds'
                  placeholder={t('modal.pinRequireSeconds')}
                  label={t('modal.pinRequireSeconds')}
                  type={'number'}
                  containerWidth={'93%'}
                  onChange={handleFboData}
                  value={fboData?.pin_require_seconds ?? ''}
                  maxwidth={'100%'}
                  disabled={
                    !fboData?.is_pin_enabled ||
                    isFieldDisabled('pin_require_seconds')
                  }
                />
                {pinRequireSecondsError && (
                  <span
                    style={{
                      color: '#EB5757',
                      fontSize: 12,
                      marginTop: 4,
                      display: 'block',
                    }}
                  >
                    {pinRequireSecondsError}
                  </span>
                )}
                <Input
                  id='inactivity_timeout_web'
                  name='inactivity_timeout_web'
                  placeholder={t('modal.inactivityTimeoutWeb')}
                  label={t('modal.inactivityTimeoutWeb')}
                  type={'number'}
                  containerWidth={'93%'}
                  onChange={handleFboData}
                  defaultValue={
                    currentItem ? currentItem?.inactivity_timeout_web : ''
                  }
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('inactivity_timeout_web')}
                />
                {/* area_director and svp */}
                {
                  !currentItem?.hidden_fields?.includes('area_director') && (
                    <Input
                      id='area_director'
                      name='area_director'
                      placeholder={t('fboProfile.fields.areaDirectorPlaceholder')}
                      label={t('fboProfile.fields.areaDirector')}
                      type={'text'}
                      containerWidth={'93%'}
                      onChange={areaDirectorHandler}
                      defaultValue={currentItem ? currentItem?.area_director : ''}
                      maxwidth={'100%'}
                      disabled={isFieldDisabled('area_director')}
                    />
                  )
                }
                {
                  !currentItem?.hidden_fields?.includes('svp') && (
                    <Input
                  id='svp'
                  name='svp'
                  placeholder={t('fboProfile.fields.svpPlaceholder')}
                  label={t('fboProfile.fields.svp')}
                  type={'text'}
                  containerWidth={'93%'}
                  onChange={svpHandler}
                  defaultValue={currentItem ? currentItem?.svp : ''}
                  maxwidth={'100%'}
                  disabled={isFieldDisabled('svp')}
                />
                  )
                }
              </div>
            )
          }

          {photo !== null && type === 'agancy' && (
            <div className='crop-photo-container'>
              <img className='crop-photo' src={photo} alt='' />
            </div>
          )}
          {type === 'agancy' && (
            <div className='file'>
              <input
                onChange={cropperHandler}
                type='file'
                id={'logo'}
                accept='image/png, image/jpeg'
                className='unused'
              />
              <label htmlFor={'logo'}>
                <span>
                  <img src='/assets/icons/camera.svg' alt='camera' />
                </span>
                <p>{t('modal.chooseFile')}</p>
              </label>
            </div>
          )}
        </div>
        <div className='modal__content-bottom'>
          <button className='close' onClick={exit}>
            {t('modal.close')}
          </button>
          <button className='save' onClick={submitCreateInspection}>
            {t('modal.save')}
          </button>
        </div>
      </div>
      <div className='modal__bg'></div>
    </div>
  );
}
